import type { PDFDocumentProxy } from 'pdfjs-dist';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import type { CustomTextRenderer } from 'react-pdf/dist/esm/shared/types.js';
import LoadingComponent from 'src/components/Loading';
import PDFControlPanel from 'src/components/PDF/PDFControlPanel/PDFControlPanel';
import useStyles from 'src/components/PDF/PDFPreviewStyles';
import usePDFSearch from 'src/components/PDF/usePDFSearch';
import './PDFPreviewStyles.css';

// Use a CDN path with the correct version and .mjs extension for react-pdf v9
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFPreviewProps {
  pdfUrl: string;
  nextPdfUrl?: string;
  prevPdfUrl?: string;
  downloadLink?: string;
  isOnMediaPage?: boolean;
}

const PDFPreview: React.FC<PDFPreviewProps> = ({ pdfUrl, nextPdfUrl, prevPdfUrl, downloadLink, isOnMediaPage }) => {
  const classes = useStyles({ isOnMediaPage: isOnMediaPage ?? false });
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [rotation, setRotation] = useState<number>(0);
  const [pageWidth, setPageWidth] = useState<number>(0);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);

  const search = usePDFSearch(pdfDocument, setPageNumber);

  useEffect(() => {
    const preloadPDF = async (url: string): Promise<void> => {
      if (!url) return;
      try {
        const response = await fetch(url, { method: 'HEAD' });
        if (response.ok) {
          const iframe = document.createElement('iframe');
          iframe.style.display = 'none';
          iframe.src = url;
          document.body.appendChild(iframe);
          setTimeout(() => document.body.removeChild(iframe), 5000);
        }
      } catch (error) {
        console.error('Error preloading PDF:', error);
      }
    };

    if (nextPdfUrl) preloadPDF(nextPdfUrl);
    if (prevPdfUrl) preloadPDF(prevPdfUrl);
  }, [nextPdfUrl, prevPdfUrl]);

  const onDocumentLoadSuccess = (pdf: PDFDocumentProxy): void => {
    setNumPages(pdf.numPages);
    setPdfDocument(pdf);
    setPageNumber(1);
    setPageWidth(window.innerWidth);
    setIsLoaded(true);
  };

  const rotateClockwise = (): void => {
    setRotation(rotation + 90);
  };

  const rotateCounterclockwise = (): void => {
    setRotation(rotation - 90);
  };

  // Custom text renderer that highlights search matches
  const textRenderer: CustomTextRenderer = useCallback(
    (textItem) => {
      if (!search.searchText || search.searchText.length < 2) {
        return textItem.str;
      }

      const text = textItem.str;
      const query = search.searchText;
      const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escaped})`, 'gi');

      const activeMatch = search.currentMatch;
      const isActiveItem =
        activeMatch && activeMatch.pageIndex === textItem.pageIndex && activeMatch.itemIndex === textItem.itemIndex;

      let occurrenceCounter = 0;
      return text.replace(regex, (match) => {
        const isActive = isActiveItem && occurrenceCounter === activeMatch.occurrenceInItem;
        occurrenceCounter++;
        const cls = isActive ? 'pdf-search-highlight-active' : 'pdf-search-highlight';
        return `<mark class="${cls}">${match}</mark>`;
      });
    },
    [search.searchText, search.currentMatch],
  );

  return (
    <div className={classes.pdfWrapper}>
      <div>
        <div
          className={classes.pdfDocument}
          style={{
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
          }}
        >
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            noData="Loading..."
            renderMode="canvas"
            loading={<LoadingComponent />}
          >
            <div className={classes.controlPanel}>
              <PDFControlPanel
                rotateClockwise={rotateClockwise}
                rotateCounterclockwise={rotateCounterclockwise}
                numPages={numPages}
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
                scale={scale}
                setScale={setScale}
                pdfUrl={pdfUrl}
                downloadLink={downloadLink}
                isOnMediaPage={isOnMediaPage}
                searchText={search.searchText}
                setSearchText={search.setSearchText}
                matchCount={search.matches.length}
                currentMatchIndex={search.currentMatchIndex}
                goToNextMatch={search.goToNextMatch}
                goToPreviousMatch={search.goToPreviousMatch}
                isSearching={search.isExtracting}
              />
            </div>
            <Page
              className={classes.pdfPage}
              pageNumber={pageNumber}
              rotate={rotation}
              scale={scale}
              width={isOnMediaPage ? Math.max(pageWidth * 0.5, 390) : Math.max(pageWidth * 0.6, 390)}
              renderTextLayer={true}
              customTextRenderer={search.searchText.length >= 2 ? textRenderer : undefined}
            />
          </Document>
        </div>
      </div>
    </div>
  );
};

export default PDFPreview;
